import * as React from 'react'
import { File as FileIcon, Upload, X } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { cn } from '../../../lib/utils'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface FileDropZoneProps {
  accept?: string
  label: string
  helperText: string
  onFilesSelected: (files: FileList) => void
  onFilesCleared?: () => void
  allowFolderSelection?: boolean
  /**
   * Pre-stage a file (e.g. one already chosen in the ingest wizard). The
   * dropzone shows its file chip immediately so the user can submit without a
   * second select.
   */
  initialFile?: File | null
  /** Pre-stage all files for multi-file wizard sources. */
  initialFiles?: File[]
}

const FileDropZone = ({
  accept,
  allowFolderSelection = false,
  helperText,
  label,
  onFilesSelected,
  onFilesCleared,
  initialFile,
  initialFiles
}: FileDropZoneProps) => {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)
  const folderInputRef = React.useRef<HTMLInputElement | null>(null)
  const [isDragActive, setIsDragActive] = React.useState(false)
  const [stagedFiles, setStagedFiles] = React.useState<File[]>(
    initialFiles?.length ? initialFiles : initialFile ? [initialFile] : []
  )

  React.useEffect(() => {
    setStagedFiles(initialFiles?.length ? initialFiles : initialFile ? [initialFile] : [])
  }, [initialFile, initialFiles])

  React.useEffect(() => {
    if (!folderInputRef.current || !allowFolderSelection) return
    folderInputRef.current.setAttribute('webkitdirectory', '')
    folderInputRef.current.setAttribute('directory', '')
  }, [allowFolderSelection])

  const handleFileList = (files: FileList | null) => {
    if (files && files.length > 0) {
      setStagedFiles(Array.from(files))
      onFilesSelected(files)
    }
  }

  const handleClear = () => {
    setStagedFiles([])
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (folderInputRef.current) folderInputRef.current.value = ''
    onFilesCleared?.()
  }

  const hasStaged = stagedFiles.length > 0
  const dataState = hasStaged ? 'staged' : isDragActive ? 'drag' : 'idle'

  return (
    <div
      data-state={dataState}
      onDragOver={(event) => {
        event.preventDefault()
        setIsDragActive(true)
      }}
      onDragLeave={() => setIsDragActive(false)}
      onDrop={(event) => {
        event.preventDefault()
        setIsDragActive(false)
        handleFileList(event.dataTransfer.files)
      }}
      className={cn(
        'min-w-0 rounded-md border-2 border-dashed p-3 transition-colors',
        'border-line-2 bg-bg-elev',
        'data-[state=drag]:border-brand data-[state=drag]:bg-brand-soft',
        'data-[state=staged]:border-st-run data-[state=staged]:bg-st-run-bg'
      )}
    >
      <div className="flex min-w-0 flex-col items-start gap-2">
        <div className="text-sm font-semibold text-ink">{label}</div>
        <div className="text-xs text-ink-3">{helperText}</div>

        {hasStaged ? (
          <div className="flex w-full min-w-0 flex-col gap-2">
            {stagedFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex w-full min-w-0 items-center gap-2"
                title={file.name}
              >
                <span className="inline-flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden rounded-sm border border-st-run bg-bg-elev px-2 py-0.5 font-mono text-xs text-ink">
                  <FileIcon className="size-3 shrink-0" />
                  <span className="min-w-0 truncate">{file.name}</span>
                </span>
                <span className="shrink-0 text-xs text-ink-3">{formatFileSize(file.size)}</span>
              </div>
            ))}
            <div className="flex flex-row gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="text-xs"
                onClick={() => fileInputRef.current?.click()}
              >
                Change File
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={handleClear}
                aria-label="Clear selected files"
              >
                <X />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-row gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-brand/30 bg-brand-soft text-xs text-brand hover:bg-brand-soft/70 hover:text-brand"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload />
              Select Files
            </Button>
            {allowFolderSelection ? (
              <Button
                size="sm"
                variant="secondary"
                className="text-xs"
                onClick={() => folderInputRef.current?.click()}
              >
                Select Folder
              </Button>
            ) : null}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        hidden
        multiple
        onChange={(event) => handleFileList(event.target.files)}
      />
      <input
        ref={folderInputRef}
        type="file"
        hidden
        multiple
        onChange={(event) => handleFileList(event.target.files)}
      />
    </div>
  )
}

export default FileDropZone
